import React, { useState, useEffect } from 'react';
import AddFastForm from '../components/AddFastForm';
import FastsList from '../components/FastsList';
import Settings from '../components/Settings';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { CalendarIcon, ClockIcon, MoonIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { doc, getDoc } from 'firebase/firestore';
import { createUserProfile } from '../firebase/collections';

function Dashboard() {
  const [key, setKey] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isAddFastModalOpen, setIsAddFastModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [missedFasts, setMissedFasts] = useState([]);
  const [isNewUser, setIsNewUser] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleFastAdded = () => {
    setKey(prev => prev + 1);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="flex flex-col items-center space-y-4">
          <MoonIcon className="h-12 w-12 text-blue-600 animate-pulse" />
          <div className="text-sm text-gray-500">Loading your fasts...</div>
        </div>
      </div>
    );
  }

  // Only show welcome message if not authenticated AND not loading
  if (!isAuthenticated && !loading) {
    return (
      <div className="min-h-screen bg-[#fafafa] bg-opacity-95 bg-[linear-gradient(45deg,#f9f9f9_25%,transparent_25%),linear-gradient(-45deg,#f9f9f9_25%,transparent_25%),linear-gradient(45deg,transparent_75%,#f9f9f9_75%),linear-gradient(-45deg,transparent_75%,#f9f9f9_75%)] bg-[length:20px_20px]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-16 relative">
          {/* Phone Mockup - Positioned absolutely */}
          <div className="hidden lg:block absolute top-24 right-0 xl:-right-28 w-[300px]">
            <div className="relative w-[300px] h-[600px] bg-black rounded-[3rem] border-[14px] border-black shadow-xl transform rotate-[-5deg]">
              {/* Phone Screen */}
              <div className="h-full w-full bg-black rounded-[2rem] overflow-hidden flex flex-col">
                {/* Status Bar */}
                <div className="bg-black px-4 pt-3 pb-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-white">1:14</div>
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12.33 3.77c.34.3.66.59 1 .89.34-.3.66-.59 1-.89 2.73-2.43 5.94-2.43 8.67 0A5.98 5.98 0 0125 8.46c0 1.98-.77 3.86-2.16 5.25l-9.85 9.85-9.85-9.85A7.4 7.4 0 011 8.46a5.98 5.98 0 012-4.69c2.73-2.43 5.94-2.43 8.67 0l.66.59z"/>
                        </svg>
                        <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                        </svg>
                        <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M2 22h20V2L2 22z"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Back Button and Contact */}
                <div className="bg-black px-4 py-2 flex items-center">
                  <button className="text-blue-500 flex items-center">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <div className="flex-1 flex flex-col items-center">
                    <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm">RT</span>
                    </div>
                    <div className="text-white text-sm mt-1">Ramadan Tracker</div>
                  </div>
                </div>

                {/* Message Thread */}
                <div className="flex-1 p-4 bg-black">
                  <div className="text-sm text-gray-500 text-center mb-4">Text Message • SMS</div>
                  <div className="text-sm text-gray-500 text-center">Today 12:14 AM</div>
                  <div className="bg-blue-600 text-white p-4 rounded-2xl max-w-[85%] mb-2 mt-4">
                    <p className="text-sm">Assalamu alaikum! Just a friendly reminder that you have 2 makeup fasts remaining. The blessed White Days are coming up next week (13-15th of the lunar month) - a perfect time to complete them! 🌙</p>
                  </div>
                  
                  
                </div>

                {/* Message Input */}
                <div className="bg-black px-4 pb-6 pt-2">
                  <div className="flex items-center space-x-2">
                    <button className="w-7 h-7 rounded-full bg-gray-800 flex items-center justify-center text-gray-400">
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-4-8v2h8v-2H8z"/>
                      </svg>
                    </button>
                    <div className="flex-1 bg-gray-800 rounded-full px-4 py-1.5 text-gray-400 text-sm">
                      Text Message
                    </div>
                    <button className="w-7 h-7 flex items-center justify-center text-gray-400">
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                        <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Home Indicator */}
                <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-white rounded-full"></div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto space-y-6 md:space-y-8">
            <div className="text-center mb-8 md:mb-12">
              <div className="flex justify-center mb-4 md:mb-6">
                <div className="bg-white p-3 md:p-4 rounded-full shadow-md">
                  <MoonIcon className="h-12 w-12 md:h-16 md:w-16 text-[#F5B100]" />
                </div>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-black mb-3 md:mb-4">
                Welcome to <span className="text-[#F5B100]">Ramadan</span> Makeup Tracker
              </h1>
              <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
                No more guessing. Know exactly how many fasts to make up before next Ramadan.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-8 mb-8 md:mb-12">
              <div className="bg-white rounded-xl shadow-md p-6 md:p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-3 bg-[#FFF8E7] rounded-lg">
                    <PhoneIcon className="h-6 w-6 text-[#F5B100]" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-semibold text-black">Get Started</h2>
                </div>
                <p className="text-gray-600 text-sm md:text-base mb-6">
                  Sign in with your phone number to access all features and start tracking your fasts.
                </p>
                <button
                  onClick={() => setIsAddFastModalOpen(true)}
                  className="bg-[#F5B100] text-white px-4 md:px-6 py-3 rounded-lg shadow-md hover:bg-[#E5A500] transition-colors duration-200 flex items-center space-x-2 group w-full justify-center"
                >
                  <PhoneIcon className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
                  <span className="font-medium">Continue with Phone</span>
                </button>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6 md:p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-3 bg-[#FFF8E7] rounded-lg">
                    <CalendarIcon className="h-6 w-6 text-[#F5B100]" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-semibold text-black">Features</h2>
                </div>
                <ul className="space-y-3 md:space-y-4 text-sm md:text-base">
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-[#F5B100] mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-600">Track missed fasts with detailed notes</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-[#F5B100] mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-600">Get suggested makeup dates</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-[#F5B100] mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-600">Monitor your progress with statistics</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-[#F5B100] mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-600">Receive optional reminders for makeup fasts</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 md:p-10">
              <h3 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6 text-center text-black">
                Why Track Your <span className="text-[#F5B100]">Makeup Fasts</span>?
              </h3>
              <p className="mb-6 md:mb-10 text-gray-600 text-center text-base md:text-lg px-4">
                Keeping track of missed fasts helps ensure you complete them before the next Ramadan. Our app makes it easy to manage your makeup fasts and stay organized.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
                <div className="bg-[#FFF8E7] rounded-xl p-5 md:p-6 hover:bg-[#FFF3D6] transition-colors duration-200">
                  <div className="flex justify-center mb-3 md:mb-4">
                    <CalendarIcon className="h-7 w-7 md:h-8 md:w-8 text-[#F5B100]" />
                  </div>
                  <h4 className="text-lg font-semibold mb-2 text-center text-black">Accurate Tracking</h4>
                  <p className="text-gray-600 text-center text-sm md:text-base">Never guess about missed fasts again. Keep a precise count of your makeup days</p>
                </div>
                <div className="bg-[#FFF8E7] rounded-xl p-5 md:p-6 hover:bg-[#FFF3D6] transition-colors duration-200">
                  <div className="flex justify-center mb-3 md:mb-4">
                    <ClockIcon className="h-7 w-7 md:h-8 md:w-8 text-[#F5B100]" />
                  </div>
                  <h4 className="text-lg font-semibold mb-2 text-center text-black">Friendly Reminders</h4>
                  <p className="text-gray-600 text-center text-sm md:text-base">Get gentle reminders throughout the year before next Ramadan approaches</p>
                </div>
                <div className="bg-[#FFF8E7] rounded-xl p-5 md:p-6 hover:bg-[#FFF3D6] transition-colors duration-200">
                  <div className="flex justify-center mb-3 md:mb-4">
                    <MoonIcon className="h-7 w-7 md:h-8 md:w-8 text-[#F5B100]" />
                  </div>
                  <h4 className="text-lg font-semibold mb-2 text-center text-black">Maximize Rewards</h4>
                  <p className="text-gray-600 text-center text-sm md:text-base">Get suggestions for blessed days like White Days to multiply your rewards</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center mb-8">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-2 rounded-2xl inline-flex items-center space-x-3">
              <CalendarIcon className="h-8 w-8 text-white" />
              <h1 className="text-3xl font-bold text-white">
                Ramadan Makeup Tracker
              </h1>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Add Fast Form */}
            <div className="lg:col-span-1">
              <div className="sticky top-8 space-y-6">
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <ClockIcon className="h-5 w-5 text-blue-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      Record Missed Fast
                    </h2>
                  </div>
                  <AddFastForm onSuccess={handleFastAdded} />
                </div>
                
                {/* Settings Section */}
                <Settings />
              </div>
            </div>

            {/* Fasts List */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="border-b border-gray-100 p-4">
                  <h2 className="text-xl font-semibold text-gray-900">Your Missed Fasts</h2>
                </div>
                <div className="p-6">
                  <FastsList key={key} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard; 